import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import {
  Briefcase,
  Building2,
  Calendar,
  Clock3,
  ExternalLink,
  FileText,
  Globe,
  Heart,
  HelpCircle,
  Lightbulb,
  MapPin,
  Sparkles,
  HandHeart,
  Trophy,
  Target,
  Users,
  ArrowLeft,
  CheckCircle,
  Info,
  Edit,
  Save,
  X,
  Clock,
  DollarSign,
  Package,
  Coffee,
  Gift,
  Settings,
  Percent,
  MapPinned,
  Building,
  Tent,
  Video,
  User,
  Mail,
  Phone,
} from "lucide-react";
import { format } from "date-fns";
import { RFQGenerator } from "@/components/corporate/campaign/RFQGenerator";
import {
  updateRFQNotes,
  fetchOpportunityById,
  fetchRfqRateCard,
  acceptRfq,
  rejectRfq,
  fetchNpoPublicProfile,
} from "@/lib/api/campaign";
import { slugify } from "@/lib/utils/string";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import { UpcomingEventsTable } from "@/components/dashboard/UpcomingEventsTable";

// Types
interface Campaign {
  id: string;
  corporate_id: string;
  npo_id?: string;
  name: string;
  meta: {
    description?: string;
    goals?: string[];
    theme?: string;
    opportunities?: string[];
    locations?: {
      country: string;
      state: string;
      district: string;
      meta?: {
        volunteers: number;
        details?: string;
      };
    }[];
    additional_details?: string;
  };
  active_from: string;
  active_to: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  status?: string;
  npos?: string[]; // Array of NPO IDs that are partners for this campaign
  rfq?: {
    id: string;
    corporate_id: string;
    campaign_id: string;
    meta: {
      additional_notes?: string;
    };
    status: number;
    created_at: string;
    updated_at: string;
  };
}

interface Opportunity {
  id: string;
  name: string;
  description?: string;
  format?: string;
  focus_area?: string;
  beneficiary_type?: string;
  sgds?: string[];
  image_url?: string;
  community_need?: string;
  volunteer_support?: string;
  impact?: string;
  beneficiary_types?: string[];
  relevant_un_day?: string[];
  location?: string;
  outcome?: string;
  meta?: {
    duration?: string;
    location?: string;
    requirements?: string;
    [key: string]: any;
  };
}

// API Functions
const fetchCampaignById = async (
  token: string,
  campaignId: string
): Promise<Campaign> => {
  const url = `${
    import.meta.env.VITE_CC_API_URL
  }/v1/campaign/corporate/campaigns/${campaignId}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch campaign");
  }

  const data = await response.json();
  return data.data;
};

const generateRFQ = async (
  token: string,
  campaignId: string,
  npoIds: string[],
  additionalNotes?: string
) => {
  const url = `${
    import.meta.env.VITE_CC_API_URL
  }/v1/campaign/${campaignId}/rfq`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ npos: npoIds, additional_notes: additionalNotes }),
  });

  if (!response.ok) {
    throw new Error("Failed to generate RFQ");
  }
};

// Helper function to get currency symbol
const getCurrencySymbol = (currency: string) => {
  switch (currency) {
    case "USD":
      return "$";
    case "INR":
      return "₹";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    default:
      return currency;
  }
};

// Centralized function to calculate total cost per unit (including all fees)
const calculateTotalCostPerUnit = (responseData: any) => {
  let totalCost = responseData.cost_per_unit || 0;

  // Add additional fees if not included in base price
  if (responseData.additional_fees && !responseData.include_fees_in_base) {
    const fees = responseData.additional_fees;
    if (fees.program_management && fees.program_management.amount)
      totalCost += fees.program_management.amount;
    if (fees.material_kit && fees.material_kit.amount)
      totalCost += fees.material_kit.amount;
    if (fees.refreshments && fees.refreshments.amount)
      totalCost += fees.refreshments.amount;
    if (fees.goodies && fees.goodies.amount) totalCost += fees.goodies.amount;
  }

  return totalCost;
};

// Centralized function to calculate total additional fees
const calculateTotalAdditionalFees = (responseData: any) => {
  let totalFees = 0;

  if (responseData.additional_fees && !responseData.include_fees_in_base) {
    const fees = responseData.additional_fees;
    if (fees.program_management && fees.program_management.amount)
      totalFees += fees.program_management.amount;
    if (fees.material_kit && fees.material_kit.amount)
      totalFees += fees.material_kit.amount;
    if (fees.refreshments && fees.refreshments.amount)
      totalFees += fees.refreshments.amount;
    if (fees.goodies && fees.goodies.amount) totalFees += fees.goodies.amount;
  }

  return totalFees;
};

// Centralized function to calculate total estimated cost for all volunteers/events
const calculateTotalEstimatedCost = (
  responseData: any,
  volunteerCount: number
) => {
  let totalCostEstimate = 0;
  let eventsRequired = 1;

  // Calculate base cost according to pricing model
  if (responseData.pricing_model === "per_volunteer") {
    // For per_volunteer model, multiply unit cost by volunteer count
    totalCostEstimate = responseData.cost_per_unit * volunteerCount;

    // Add per_volunteer fees
    if (responseData.additional_fees && !responseData.include_fees_in_base) {
      const fees = responseData.additional_fees;
      if (
        fees.program_management &&
        fees.program_management.fee_type === "per_volunteer" &&
        fees.program_management.amount
      ) {
        totalCostEstimate += fees.program_management.amount * volunteerCount;
      }
      if (
        fees.material_kit &&
        fees.material_kit.fee_type === "per_volunteer" &&
        fees.material_kit.amount
      ) {
        totalCostEstimate += fees.material_kit.amount * volunteerCount;
      }
      if (
        fees.refreshments &&
        fees.refreshments.fee_type === "per_volunteer" &&
        fees.refreshments.amount
      ) {
        totalCostEstimate += fees.refreshments.amount * volunteerCount;
      }
      if (
        fees.goodies &&
        fees.goodies.fee_type === "per_volunteer" &&
        fees.goodies.amount
      ) {
        totalCostEstimate += fees.goodies.amount * volunteerCount;
      }
    }
  } else if (responseData.pricing_model === "per_event") {
    // For per_event model, calculate number of events needed
    const maxVolunteersPerEvent =
      parseInt(responseData.max_volunteers_per_event) || 0;

    if (maxVolunteersPerEvent > 0) {
      eventsRequired = Math.ceil(volunteerCount / maxVolunteersPerEvent);
      totalCostEstimate = responseData.cost_per_unit * eventsRequired;
    } else {
      totalCostEstimate = responseData.cost_per_unit;
    }

    // Add per_event fees
    if (responseData.additional_fees && !responseData.include_fees_in_base) {
      const fees = responseData.additional_fees;
      if (
        fees.program_management &&
        fees.program_management.fee_type === "per_event" &&
        fees.program_management.amount
      ) {
        totalCostEstimate += fees.program_management.amount * eventsRequired;
      }
      if (
        fees.material_kit &&
        fees.material_kit.fee_type === "per_event" &&
        fees.material_kit.amount
      ) {
        totalCostEstimate += fees.material_kit.amount * eventsRequired;
      }
      if (
        fees.refreshments &&
        fees.refreshments.fee_type === "per_event" &&
        fees.refreshments.amount
      ) {
        totalCostEstimate += fees.refreshments.amount * eventsRequired;
      }
      if (
        fees.goodies &&
        fees.goodies.fee_type === "per_event" &&
        fees.goodies.amount
      ) {
        totalCostEstimate += fees.goodies.amount * eventsRequired;
      }
    }
  } else {
    // For total program pricing
    totalCostEstimate = responseData.cost_per_unit;
  }

  // Add total_program fees (one-time fees)
  if (responseData.additional_fees && !responseData.include_fees_in_base) {
    const fees = responseData.additional_fees;
    if (
      fees.program_management &&
      fees.program_management.fee_type === "total_program" &&
      fees.program_management.amount
    ) {
      totalCostEstimate += fees.program_management.amount;
    }
    if (
      fees.material_kit &&
      fees.material_kit.fee_type === "total_program" &&
      fees.material_kit.amount
    ) {
      totalCostEstimate += fees.material_kit.amount;
    }
    if (
      fees.refreshments &&
      fees.refreshments.fee_type === "total_program" &&
      fees.refreshments.amount
    ) {
      totalCostEstimate += fees.refreshments.amount;
    }
    if (
      fees.goodies &&
      fees.goodies.fee_type === "total_program" &&
      fees.goodies.amount
    ) {
      totalCostEstimate += fees.goodies.amount;
    }
  }

  return { totalCostEstimate, eventsRequired };
};

export default function CorporateViewCampaignsPage({
  session,
  profile,
}: {
  session: any;
  profile: any;
}) {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [opportunitiesData, setOpportunitiesData] = useState<
    Record<string, Opportunity>
  >({});
  const [visibleOpportunities, setVisibleOpportunities] = useState<string[]>(
    []
  );
  const [hasMoreOpportunities, setHasMoreOpportunities] = useState(true);
  const [opportunitiesPage, setOpportunitiesPage] = useState(1);
  const OPPORTUNITIES_PER_PAGE = 6; // Number of opportunities to load per page
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const [loadingOpportunities, setLoadingOpportunities] = useState<
    Record<string, boolean>
  >({});
  const [npoProfiles, setNpoProfiles] = useState<any[]>([]);
  const [loadingNpoProfiles, setLoadingNpoProfiles] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isRateCardModalOpen, setIsRateCardModalOpen] = useState(false);
  const [selectedNpoRateCards, setSelectedNpoRateCards] = useState<any[]>([]);
  const [selectedNpoProfile, setSelectedNpoProfile] = useState<any>(null);
  // Fetch campaign data
  const {
    data: campaign,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["campaign", campaignId],
    queryFn: () =>
      fetchCampaignById(session?.access_token, campaignId as string),
    enabled: !!session?.access_token && !!campaignId,
  });

  // Update RFQ notes mutation
  const updateNotesMutation = useMutation({
    mutationFn: async () => {
      if (!session?.access_token || !campaignId) return;
      return updateRFQNotes(session.access_token, campaignId, additionalNotes);
    },
    onSuccess: () => {
      toast({
        title: "Notes Updated",
        description: "Additional notes have been updated successfully.",
      });
      setIsEditingNotes(false);
      queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to update notes",
        variant: "destructive",
      });
    },
  });

  // Function to fetch opportunity details
  const fetchOpportunityDetails = async (opportunityId: string) => {
    if (!session?.access_token) return;

    // Check if we already have this opportunity or if it's already loading
    if (
      opportunitiesData[opportunityId] ||
      loadingOpportunities[opportunityId]
    ) {
      return;
    }

    // Set loading state for this opportunity
    setLoadingOpportunities((prev) => ({ ...prev, [opportunityId]: true }));

    try {
      const result = await fetchOpportunityById(
        session.access_token,
        opportunityId
      );

      if (result && result.data) {
        // Store the opportunity data
        setOpportunitiesData((prev) => ({
          ...prev,
          [opportunityId]: result.data,
        }));
      }
    } catch (error) {
      console.error("Error fetching opportunity:", error);
    } finally {
      // Clear loading state
      setLoadingOpportunities((prev) => ({ ...prev, [opportunityId]: false }));
    }
  };

  // Function to fetch NPO public profiles
  const fetchNpoProfiles = async () => {
    if (!session?.access_token) return;

    setLoadingNpoProfiles(true);

    try {
      // Get NPO IDs either from campaign.npos or from accepted RFQ responses
      let npoIds: string[] = [];

      if (campaign?.npos && campaign.npos.length > 0) {
        // If npos field is available, use it
        npoIds = campaign.npos;
      } else if (rateCardData?.data && Array.isArray(rateCardData.data)) {
        // Otherwise, extract NPO IDs from accepted RFQ responses
        npoIds = rateCardData.data
          .filter((item: any) => item.status === 2) // Status 2 means accepted
          .map((item: any) => item.npo?.id)
          .filter(Boolean); // Remove any undefined or null values
      }

      if (npoIds.length === 0) {
        setNpoProfiles([]);
        return;
      }

      const profilePromises = npoIds.map((npoId: string) =>
        fetchNpoPublicProfile(session.access_token, npoId)
      );

      const profiles = await Promise.all(profilePromises);
      setNpoProfiles(profiles.map((profile) => profile.data));
    } catch (error) {
      console.error("Error fetching NPO profiles:", error);
      toast({
        title: "Error",
        description: "Failed to load NPO partner profiles",
        variant: "destructive",
      });
    } finally {
      setLoadingNpoProfiles(false);
    }
  };

  // Initialize visible opportunities when tab changes to opportunities
  useEffect(() => {
    if (
      activeTab === "opportunities" &&
      campaign?.meta?.opportunities &&
      campaign.meta.opportunities.length > 0
    ) {
      // Reset pagination state when tab changes to opportunities
      setOpportunitiesPage(1);
      setHasMoreOpportunities(true);

      // Initialize with first page of opportunities
      const initialOpportunities = campaign.meta.opportunities.slice(
        0,
        OPPORTUNITIES_PER_PAGE
      );
      setVisibleOpportunities(initialOpportunities);

      // Load details for initial visible opportunities
      initialOpportunities.forEach((opportunityId) => {
        if (!opportunitiesData[opportunityId]) {
          fetchOpportunityDetails(opportunityId);
        }
      });
    }
  }, [activeTab, campaign?.meta?.opportunities, session?.access_token]);

  // Load more opportunities when user scrolls
  const loadMoreOpportunities = useCallback(() => {
    if (!campaign?.meta?.opportunities || !hasMoreOpportunities) return;

    const startIndex = opportunitiesPage * OPPORTUNITIES_PER_PAGE;
    const endIndex = startIndex + OPPORTUNITIES_PER_PAGE;
    const nextOpportunities = campaign.meta.opportunities.slice(
      startIndex,
      endIndex
    );

    if (nextOpportunities.length === 0) {
      setHasMoreOpportunities(false);
      return;
    }

    setVisibleOpportunities((prev) => [...prev, ...nextOpportunities]);
    setOpportunitiesPage((prev) => prev + 1);

    // Fetch details for newly visible opportunities
    nextOpportunities.forEach((opportunityId) => {
      if (!opportunitiesData[opportunityId]) {
        fetchOpportunityDetails(opportunityId);
      }
    });
  }, [
    campaign?.meta?.opportunities,
    opportunitiesPage,
    hasMoreOpportunities,
    opportunitiesData,
    session?.access_token,
  ]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    if (activeTab !== "opportunities" || !hasMoreOpportunities) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreOpportunities();
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current = observer;

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [activeTab, hasMoreOpportunities, loadMoreOpportunities]);

  // Initialize additional notes from campaign data
  useEffect(() => {
    if (campaign?.rfq?.meta?.additional_notes) {
      setAdditionalNotes(campaign.rfq.meta.additional_notes);
    }
  }, [campaign]);

  // Handle RFQ generation
  const { mutate: generateRFQMutation, isPending: isGeneratingRFQ } =
    useMutation({
      mutationFn: (params: {
        selectedNPOs: string[];
        additionalNotes?: string;
      }) => {
        if (!campaign?.id || !session?.access_token) {
          throw new Error("Campaign ID or token not available");
        }
        return generateRFQ(
          session.access_token,
          campaign.id,
          params.selectedNPOs,
          params.additionalNotes
        );
      },
      onSuccess: () => {
        toast({
          title: "RFQ Generated",
          description: "Request for Quotation has been sent successfully.",
        });
        // Reload the page to reflect the updated RFQ status
        window.location.reload();
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: `Failed to generate RFQ: ${error.message}`,
          variant: "destructive",
        });
      },
    });

  const handleGenerateRFQ = (
    selectedNPOs: string[],
    additionalNotes?: string
  ) => {
    generateRFQMutation({ selectedNPOs, additionalNotes });
  };

  // Handle RFQ response (accept/decline)
  const [rejectReason, setRejectReason] = useState("");
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(
    null
  );

  // Sorting state for RFQ responses tables - use a map to store sorting config for each opportunity ID
  const [sortConfigMap, setSortConfigMap] = useState<{
    [opportunityId: string]: {
      key: string;
      direction: "ascending" | "descending";
    };
  }>({});

  // Function to handle sorting for a specific opportunity table
  const requestSort = (opportunityId: string, key: string) => {
    setSortConfigMap((prevMap) => {
      const currentConfig = prevMap[opportunityId] || {
        key: "cost",
        direction: "ascending",
      };

      let direction: "ascending" | "descending" = "ascending";
      if (
        currentConfig.key === key &&
        currentConfig.direction === "ascending"
      ) {
        direction = "descending";
      }

      return {
        ...prevMap,
        [opportunityId]: { key, direction },
      };
    });
  };

  // Helper function to get sort config for a specific opportunity
  const getSortConfig = (opportunityId: string) => {
    return (
      sortConfigMap[opportunityId] || { key: "cost", direction: "ascending" }
    );
  };

  // Accept RFQ mutation
  const { mutate: acceptRfqMutation, isPending: isAccepting } = useMutation({
    mutationFn: (responseId: string) => {
      if (!session?.access_token || !campaign?.rfq?.id) {
        throw new Error("RFQ ID or token not available");
      }
      return acceptRfq(session.access_token, campaign.rfq.id, responseId);
    },
    onSuccess: () => {
      toast({
        title: "Response Accepted",
        description: "The RFQ response has been accepted successfully.",
      });
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to accept RFQ response: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Reject RFQ mutation
  const { mutate: rejectRfqMutation, isPending: isRejecting } = useMutation({
    mutationFn: (params: { responseId: string; reason: string }) => {
      if (!session?.access_token || !campaign?.rfq?.id) {
        throw new Error("RFQ ID or token not available");
      }
      return rejectRfq(
        session.access_token,
        campaign.rfq.id,
        params.responseId,
        params.reason
      );
    },
    onSuccess: () => {
      toast({
        title: "Response Rejected",
        description: "The RFQ response has been rejected successfully.",
      });
      // Reset state
      setRejectReason("");
      setIsRejectDialogOpen(false);
      setSelectedResponseId(null);
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to reject RFQ response: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const handleAccept = (responseId: string) => {
    acceptRfqMutation(responseId);
  };

  const handleReject = () => {
    if (selectedResponseId) {
      rejectRfqMutation({
        responseId: selectedResponseId,
        reason: rejectReason,
      });
    }
  };

  const openRejectDialog = (responseId: string) => {
    setSelectedResponseId(responseId);
    setIsRejectDialogOpen(true);
  };

  // Function to open rate card modal
  const openRateCardModal = (npo: any) => {
    if (!rateCardData?.data) return;

    const npoRateCards = rateCardData.data.filter(
      (item: any) => item.npo && item.npo.id === npo.id
    );

    setSelectedNpoRateCards(npoRateCards);
    setSelectedNpoProfile(npo);
    setIsRateCardModalOpen(true);
  };

  // Fetch rate card data for RFQ if status is "waiting"
  const {
    data: rateCardData,
    isLoading: isLoadingRateCard,
    isError: isRateCardError,
  } = useQuery({
    queryKey: ["rateCard", campaign?.rfq?.id],
    queryFn: () => fetchRfqRateCard(session?.access_token, campaign?.rfq?.id),
    enabled:
      !!session?.access_token &&
      !!campaign?.rfq?.id &&
      (campaign.status === "waiting" || campaign.status === "accepted"),
  });

  // Load missing opportunity details for RFQ responses
  useEffect(() => {
    if (
      rateCardData?.data &&
      Array.isArray(rateCardData.data) &&
      rateCardData.data.length > 0
    ) {
      console.log(rateCardData.data.map((item: any) => item.status));
      // Get all unique opportunity IDs from rate card responses
      const opportunityIds = new Set(
        rateCardData.data.map((item: any) => item.opportunity_id)
      );

      // Find which ones are missing from our cache
      const missingOpportunityIds = Array.from(opportunityIds).filter(
        (id: string) => !opportunitiesData[id]
      );

      // Fetch missing opportunity details
      if (missingOpportunityIds.length > 0 && session?.access_token) {
        missingOpportunityIds.forEach((opportunityId: string) => {
          fetchOpportunityDetails(opportunityId);
        });
      }
    }
  }, [rateCardData, session?.access_token]);

  // Load NPO profiles when tab changes to partners
  useEffect(() => {
    if (activeTab === "partners" && npoProfiles.length === 0) {
      fetchNpoProfiles();
    }
  }, [
    activeTab,
    campaign?.npos,
    rateCardData,
    npoProfiles.length,
    session?.access_token,
  ]);

  // Fetch campaign events
  const {
    data: eventsData,
    isLoading: isLoadingEvents,
    isError: isEventsError,
  } = useQuery({
    queryKey: ["campaign-events", campaignId, currentPage],
    queryFn: async () => {
      if (!session?.access_token || !campaignId)
        throw new Error("Missing session or campaign ID");

      const url = `${
        import.meta.env.VITE_CC_API_URL
      }/v1/events/campaign/${campaignId}?page=${currentPage}&limit=10`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch campaign events");
      }

      return response.json();
    },
    enabled: !!session?.access_token && !!campaignId && activeTab === "events",
  });

  // Handle page change for events pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/dashboard/campaigns")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Campaigns
          </Button>
          <Skeleton className="h-8 w-64" />
        </div>

        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-full max-w-md" />
            <Skeleton className="h-4 w-full max-w-sm mt-2" />
          </CardHeader>
          <CardContent className="space-y-6">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError || !campaign) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4 mb-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/dashboard/campaigns")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Campaigns
          </Button>
        </div>

        <Card className="border-destructive">
          <CardHeader>
            <CardTitle>Error Loading Campaign</CardTitle>
            <CardDescription>
              We couldn't load the campaign details. Please try again or contact
              support.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {error instanceof Error
                ? error.message
                : "Unknown error occurred"}
            </p>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate("/dashboard/campaigns")}>
              Return to Campaigns
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  const startDate = new Date(campaign.active_from);
  const endDate = new Date(campaign.active_to);
  const isActive =
    campaign.is_active && new Date() >= startDate && new Date() <= endDate;
  const status = campaign.status || "created"; // Default to "created" if status is not present

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/dashboard/campaigns")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Campaigns
          </Button>
          <h1 className="text-2xl font-bold">{campaign.name}</h1>
          <Badge variant={isActive ? "default" : "secondary"}>
            {isActive ? "Active" : "Inactive"}
          </Badge>
          <Badge variant="outline">{status}</Badge>
        </div>

        {isActive && status === "accepted" && (
          <Button
            variant="default"
            size="sm"
            onClick={() =>
              window.open(
                `${import.meta.env.VITE_CC_APP_URL}/corporate/${slugify(
                  profile?.org_name?.toLowerCase()
                )}/events/${campaign.id}`,
                "_blank"
              )
            }
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Share Campaign
          </Button>
        )}
      </div>

      <Tabs
        defaultValue="overview"
        className="w-full"
        onValueChange={setActiveTab}
      >
        <TabsList
          className={`grid w-full ${
            status === "accepted" ? "grid-cols-5" : "grid-cols-4"
          }`}
        >
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="locations">Locations</TabsTrigger>
          <TabsTrigger value="opportunities">Opportunities</TabsTrigger>
          <TabsTrigger value="events">Events</TabsTrigger>
          {status === "accepted" && (
            <TabsTrigger value="partners">NPO Partners</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Campaign Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Description
                    </h3>
                    <p className="mt-1">
                      {campaign.meta.description || "No description provided"}
                    </p>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">
                      Theme
                    </h3>
                    <p className="mt-1">
                      {campaign.meta.theme || "No theme specified"}
                    </p>
                  </div>

                  {campaign.meta.goals && campaign.meta.goals.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground">
                        Goals
                      </h3>
                      <ul className="mt-1 list-disc pl-5">
                        {campaign.meta.goals.map((goal, index) => (
                          <li key={index}>{goal}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {campaign.meta.additional_details && (
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground">
                        Additional Details
                      </h3>
                      <p className="mt-1">{campaign.meta.additional_details}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 mr-2 text-primary" />
                    <div>
                      <h3 className="text-sm font-medium">Campaign Duration</h3>
                      <p className="text-sm text-muted-foreground">
                        {format(startDate, "MMMM d, yyyy")} -{" "}
                        {format(endDate, "MMMM d, yyyy")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2 text-primary" />
                    <div>
                      <h3 className="text-sm font-medium">Status</h3>
                      <p className="text-sm text-muted-foreground">
                        {isActive ? "Active" : "Inactive"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Info className="h-5 w-5 mr-2 text-primary" />
                    <div>
                      <h3 className="text-sm font-medium">Campaign ID</h3>
                      <p className="text-sm text-muted-foreground">
                        {campaign.id}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Clock className="h-5 w-5 mr-2 text-primary" />
                    <div>
                      <h3 className="text-sm font-medium">Created At</h3>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(campaign.created_at), "MMMM d, yyyy")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Clock className="h-5 w-5 mr-2 text-primary" />
                    <div>
                      <h3 className="text-sm font-medium">Last Updated</h3>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(campaign.updated_at), "MMMM d, yyyy")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="locations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Campaign Locations</CardTitle>
              <CardDescription>
                Locations where this campaign will be active
              </CardDescription>
            </CardHeader>
            <CardContent>
              {campaign.meta.locations && campaign.meta.locations.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Country</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>District</TableHead>
                      <TableHead>Volunteers</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campaign.meta.locations.map((location, index) => (
                      <TableRow key={index}>
                        <TableCell>{location.country}</TableCell>
                        <TableCell>{location.state}</TableCell>
                        <TableCell>{location.district}</TableCell>
                        <TableCell>{location.meta?.volunteers || 0}</TableCell>
                        <TableCell>{location.meta?.details || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableCaption>
                    Total {campaign.meta.locations.length} location
                    {campaign.meta.locations.length !== 1 ? "s" : ""}
                  </TableCaption>
                </Table>
              ) : (
                <div className="text-center py-6">
                  <MapPin className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
                  <p className="mt-2 text-muted-foreground">
                    No locations specified for this campaign
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="opportunities" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Campaign Opportunities</CardTitle>
              <CardDescription>
                Opportunities associated with this campaign
              </CardDescription>
            </CardHeader>
            <CardContent>
              {campaign.meta.opportunities &&
              campaign.meta.opportunities.length > 0 ? (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    This campaign includes {campaign.meta.opportunities.length}{" "}
                    opportunity/opportunities.
                  </p>
                  <ScrollArea className="h-[500px]">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {visibleOpportunities.map((opportunityId, index) => (
                        <Card
                          key={index}
                          className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() =>
                            window.open(
                              `/opportunities/${opportunityId}`,
                              "_blank"
                            )
                          }
                        >
                          {loadingOpportunities[opportunityId] ? (
                            <div className="space-y-3">
                              <Skeleton className="h-40 w-full rounded-md" />
                              <Skeleton className="h-5 w-3/4" />
                              <Skeleton className="h-4 w-full" />
                              <Skeleton className="h-4 w-full" />
                              <div className="flex gap-2">
                                <Skeleton className="h-5 w-16" />
                                <Skeleton className="h-5 w-16" />
                              </div>
                            </div>
                          ) : opportunitiesData[opportunityId] ? (
                            <div className="space-y-3">
                              {opportunitiesData[opportunityId].image_url ? (
                                <div className="w-full h-40 overflow-hidden rounded-md">
                                  <img
                                    src={
                                      opportunitiesData[opportunityId].image_url
                                    }
                                    alt={opportunitiesData[opportunityId].name}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                              ) : (
                                <div className="w-full h-40 bg-slate-100 rounded-md flex items-center justify-center">
                                  <Briefcase className="h-10 w-10 text-slate-400" />
                                </div>
                              )}

                              <h3 className="font-medium text-base line-clamp-1">
                                {opportunitiesData[opportunityId].name}
                              </h3>

                              {opportunitiesData[opportunityId]
                                .community_need && (
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground flex items-center">
                                    <HelpCircle className="h-3.5 w-3.5 mr-1.5" />
                                    Community Need
                                  </p>
                                  <p className="text-sm line-clamp-2">
                                    {
                                      opportunitiesData[opportunityId]
                                        .community_need
                                    }
                                  </p>
                                </div>
                              )}

                              {opportunitiesData[opportunityId].impact && (
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground flex items-center">
                                    <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                                    Impact
                                  </p>
                                  <p className="text-sm line-clamp-2">
                                    {opportunitiesData[opportunityId].impact}
                                  </p>
                                </div>
                              )}

                              <div className="grid grid-cols-2 gap-2">
                                {opportunitiesData[opportunityId]
                                  .volunteer_support && (
                                  <div>
                                    <p className="text-xs font-medium text-muted-foreground flex items-center">
                                      <HandHeart className="h-3.5 w-3.5 mr-1.5" />
                                      Support Type
                                    </p>
                                    <p className="text-sm">
                                      {
                                        opportunitiesData[opportunityId]
                                          .volunteer_support
                                      }
                                    </p>
                                  </div>
                                )}

                                {opportunitiesData[opportunityId].outcome && (
                                  <div>
                                    <p className="text-xs font-medium text-muted-foreground flex items-center">
                                      <Trophy className="h-3.5 w-3.5 mr-1.5" />
                                      Outcome
                                    </p>
                                    <p className="text-sm">
                                      {opportunitiesData[opportunityId].outcome}
                                    </p>
                                  </div>
                                )}
                              </div>

                              <div className="flex flex-wrap gap-2">
                                {opportunitiesData[opportunityId].format && (
                                  <Badge
                                    variant="outline"
                                    className="text-xs flex items-center gap-1"
                                  >
                                    <Building2 className="h-3 w-3" />
                                    {opportunitiesData[
                                      opportunityId
                                    ].format.replace(/_/g, " ")}
                                  </Badge>
                                )}
                                {opportunitiesData[opportunityId]
                                  .focus_area && (
                                  <Badge
                                    variant="outline"
                                    className="text-xs flex items-center gap-1"
                                  >
                                    <Target className="h-3 w-3" />
                                    {opportunitiesData[
                                      opportunityId
                                    ].focus_area.replace(/_/g, " ")}
                                  </Badge>
                                )}
                                {opportunitiesData[opportunityId].location && (
                                  <Badge
                                    variant="outline"
                                    className="text-xs flex items-center gap-1"
                                  >
                                    <MapPin className="h-3 w-3" />
                                    {opportunitiesData[opportunityId].location}
                                  </Badge>
                                )}
                              </div>

                              {opportunitiesData[opportunityId]
                                .beneficiary_types &&
                                opportunitiesData[opportunityId]
                                  .beneficiary_types.length > 0 && (
                                  <div className="border-t pt-2">
                                    <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center">
                                      <Users className="h-3.5 w-3.5 mr-1.5" />
                                      Beneficiary Types
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                      {opportunitiesData[
                                        opportunityId
                                      ].beneficiary_types.map((type, idx) => (
                                        <Badge
                                          key={idx}
                                          variant="secondary"
                                          className="text-xs"
                                        >
                                          {type}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}

                              {opportunitiesData[opportunityId]
                                .relevant_un_day &&
                                opportunitiesData[opportunityId].relevant_un_day
                                  .length > 0 && (
                                  <div className="border-t pt-2">
                                    <p className="text-xs font-medium text-muted-foreground mb-1 flex items-center">
                                      <Globe className="h-3.5 w-3.5 mr-1.5" />
                                      Relevant UN Days
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                      {opportunitiesData[
                                        opportunityId
                                      ].relevant_un_day.map((day, idx) => (
                                        <Badge
                                          key={idx}
                                          variant="secondary"
                                          className="text-xs"
                                        >
                                          {day}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center h-40 space-y-3">
                              <FileText className="h-10 w-10 text-primary/50" />
                              <div className="text-center">
                                <p className="font-medium">Opportunity ID</p>
                                <p className="text-sm text-muted-foreground">
                                  {opportunityId}
                                </p>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  fetchOpportunityDetails(opportunityId);
                                }}
                              >
                                Load Details
                              </Button>
                            </div>
                          )}
                        </Card>
                      ))}
                    </div>
                    {hasMoreOpportunities && (
                      <div
                        ref={loadMoreRef}
                        className="w-full flex justify-center py-4"
                      >
                        <div className="flex items-center justify-center">
                          <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
                          <span className="text-sm text-muted-foreground">
                            Loading more opportunities...
                          </span>
                        </div>
                      </div>
                    )}
                  </ScrollArea>
                </div>
              ) : (
                <div className="text-center py-6">
                  <Target className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
                  <p className="mt-2 text-muted-foreground">
                    No opportunities associated with this campaign
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Campaign Events</CardTitle>
              <CardDescription>
                All events associated with this campaign
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingEvents ? (
                <div className="space-y-4">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-64 w-full" />
                </div>
              ) : isEventsError ? (
                <div className="text-center py-12 bg-slate-50 rounded-md">
                  <Info className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">
                    Error Loading Events
                  </h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    We couldn't load the events for this campaign. Please try
                    again or contact support.
                  </p>
                </div>
              ) : eventsData?.data && eventsData.data.length > 0 ? (
                <UpcomingEventsTable
                  events={eventsData.data}
                  profile={profile}
                  session={session}
                  pagination={eventsData.pagination}
                  onPageChange={handlePageChange}
                />
              ) : (
                <div className="text-center py-12 bg-slate-50 rounded-md">
                  <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No Events Found</h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    There are no events associated with this campaign yet.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {status === "accepted" && (
          <TabsContent value="partners" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>NPO Partners</CardTitle>
                <CardDescription>
                  Organizations partnering with this campaign
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingNpoProfiles ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((_, index) => (
                      <Card key={index} className="overflow-hidden">
                        <CardHeader className="pb-2">
                          <div className="flex items-center space-x-4">
                            <Skeleton className="h-12 w-12 rounded-full" />
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-32" />
                              <Skeleton className="h-3 w-24" />
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <Skeleton className="h-20 w-full" />
                            <Skeleton className="h-16 w-full" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : campaign?.npos &&
                  campaign.npos.length > 0 &&
                  npoProfiles.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {npoProfiles.map((npo, index) => (
                      <Card
                        key={index}
                        className="overflow-hidden hover:shadow-md transition-shadow"
                      >
                        <CardHeader className="pb-2">
                          <div className="flex items-center space-x-4">
                            {npo.logo_url ? (
                              <img
                                src={npo.logo_url}
                                alt={npo.org_name}
                                className="h-10 w-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <Building2 className="h-5 w-5 text-primary" />
                              </div>
                            )}
                            <div>
                              <CardTitle className="text-md">
                                {npo.org_name || "Unknown NPO"}
                              </CardTitle>
                              <CardDescription className="flex items-center text-xs">
                                <Globe className="h-3 w-3 mr-1" />
                                {npo.website ? (
                                  <a
                                    href={npo.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:underline flex items-center"
                                  >
                                    {npo.website}{" "}
                                    <ExternalLink className="h-3 w-3 ml-1" />
                                  </a>
                                ) : (
                                  "No website provided"
                                )}
                              </CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-2 space-y-3">
                          {npo.description && (
                            <div>
                              <h4 className="text-xs font-medium mb-1 flex items-center">
                                <Info className="h-3 w-3 mr-1 text-primary" />
                                About
                              </h4>
                              <p className="text-xs line-clamp-2">
                                {npo.description}
                              </p>
                            </div>
                          )}

                          {/* Rate Card Information */}
                          {rateCardData &&
                            rateCardData.data &&
                            rateCardData.data.length > 0 && (
                              <div className="bg-slate-50 p-2 rounded-md">
                                <div className="flex justify-between items-center mb-2">
                                  <h4 className="text-xs font-medium flex items-center">
                                    <DollarSign className="h-3.5 w-3.5 mr-1 text-primary" />
                                    Rate Cards
                                  </h4>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs"
                                    onClick={() => openRateCardModal(npo)}
                                  >
                                    View Details
                                  </Button>
                                </div>
                              </div>
                            )}

                          <div className="grid grid-cols-1 gap-2">
                            {/* Contact Information */}
                            {(npo.email || npo.phone) && (
                              <div className="bg-slate-50 p-2 rounded-md">
                                <h4 className="text-xs font-medium mb-1 flex items-center">
                                  <User className="h-3.5 w-3.5 mr-1 text-primary" />
                                  Contact
                                </h4>
                                <div className="space-y-1 text-xs">
                                  {npo.email && (
                                    <div className="flex items-center">
                                      <Mail className="h-3 w-3 mr-1 text-muted-foreground" />
                                      <a
                                        href={`mailto:${npo.email}`}
                                        className="hover:underline"
                                      >
                                        {npo.email}
                                      </a>
                                    </div>
                                  )}
                                  {npo.phone && (
                                    <div className="flex items-center">
                                      <Phone className="h-3 w-3 mr-1 text-muted-foreground" />
                                      <a
                                        href={`tel:${npo.phone}`}
                                        className="hover:underline"
                                      >
                                        {npo.phone}
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Focus Areas */}
                            {npo.focus_areas && npo.focus_areas.length > 0 && (
                              <div>
                                <h4 className="text-xs font-medium mb-1 flex items-center">
                                  <Target className="h-3.5 w-3.5 mr-1 text-primary" />
                                  Focus Areas
                                </h4>
                                <div className="flex flex-wrap gap-1">
                                  {npo.focus_areas
                                    .slice(0, 3)
                                    .map((area: string, idx: number) => (
                                      <Badge
                                        key={idx}
                                        variant="secondary"
                                        className="text-xs"
                                      >
                                        {area}
                                      </Badge>
                                    ))}
                                  {npo.focus_areas.length > 3 && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      +{npo.focus_areas.length - 3} more
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Location */}
                          {(npo.city || npo.state || npo.country) && (
                            <div className="flex items-center text-xs">
                              <MapPin className="h-3 w-3 mr-1 text-primary" />
                              <span className="text-xs flex items-center">
                                {[npo.city, npo.state, npo.country]
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground opacity-50" />
                    <p className="mt-2 text-muted-foreground">
                      No NPO partners available for this campaign
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Show RFQ Generator only when campaign status is "created" */}
      {status === "created" && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4">
            Generate Request for Quotation
          </h2>
          <RFQGenerator
            campaign={campaign}
            handleGenerateRFQ={handleGenerateRFQ}
            isGeneratingRFQ={isGeneratingRFQ}
            token={session?.access_token}
          />
        </div>
      )}

      {/* Show RFQ waiting message when campaign status is "waiting" */}
      {(status === "waiting" || status === "accepted") &&
        rateCardData &&
        rateCardData.data &&
        rateCardData.data.filter((item: any) => item.status === 1).length >
          0 && (
          <div className="mt-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-xl">RFQ Responses</CardTitle>
                  <CardDescription>
                    Review and compare responses from NPOs for your request for
                    quotation.
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {isLoadingRateCard ? (
                    <div className="space-y-4">
                      <Skeleton className="h-48 w-full" />
                      <Skeleton className="h-48 w-full" />
                    </div>
                  ) : isRateCardError ? (
                    <div className="bg-red-50 p-6 rounded-md text-center">
                      <div className="text-red-600 mb-2">
                        Failed to load RFQ responses
                      </div>
                      <Button
                        variant="outline"
                        onClick={() =>
                          queryClient.invalidateQueries({
                            queryKey: ["rateCard", campaign?.rfq?.id],
                          })
                        }
                      >
                        Try Again
                      </Button>
                    </div>
                  ) : rateCardData &&
                    rateCardData.data &&
                    rateCardData.data.filter((item: any) => item.status === 1)
                      .length > 0 ? (
                    <div className="space-y-8">
                      {/* Group rate cards by opportunity ID */}
                      {(() => {
                        // Create a map of opportunity ID to rate cards
                        const opportunityMap = new Map();

                        // Filter for pending status (0) and group rate cards by opportunity ID
                        const pendingRateCards = rateCardData.data.filter(
                          (item: any) => item.status === 1
                        );
                        pendingRateCards.forEach((item: any) => {
                          if (!opportunityMap.has(item.opportunity_id)) {
                            opportunityMap.set(item.opportunity_id, []);
                          }
                          opportunityMap.get(item.opportunity_id).push(item);
                        });

                        // Convert map to array for rendering
                        return Array.from(opportunityMap.entries()).map(
                          ([opportunityId, rateCards]: [string, any[]]) => {
                            // Get opportunity details if available
                            const opportunityDetails =
                              opportunitiesData[opportunityId];

                            return (
                              <div
                                key={opportunityId}
                                className="border rounded-lg overflow-hidden"
                              >
                                {/* Opportunity Header */}
                                <div className="bg-slate-50 p-4 border-b">
                                  <div className="flex items-center space-x-3">
                                    {opportunityDetails?.image_url ? (
                                      <img
                                        src={opportunityDetails.image_url}
                                        alt={opportunityDetails.name}
                                        className="h-12 w-12 rounded-md object-cover"
                                      />
                                    ) : (
                                      <div className="h-12 w-12 rounded-md bg-primary/10 flex items-center justify-center">
                                        <Briefcase className="h-6 w-6 text-primary" />
                                      </div>
                                    )}
                                    <div>
                                      <h3 className="text-lg font-medium">
                                        {opportunityDetails?.name ||
                                          `Opportunity ${opportunityId}`}
                                      </h3>
                                      <div className="flex items-center text-xs mt-1 space-x-2">
                                        {opportunityDetails?.format && (
                                          <Badge
                                            variant="outline"
                                            className="text-xs flex items-center gap-1"
                                          >
                                            <Building2 className="h-3 w-3" />
                                            {opportunityDetails.format.replace(
                                              /_/g,
                                              " "
                                            )}
                                          </Badge>
                                        )}
                                        {opportunityDetails?.focus_area && (
                                          <Badge
                                            variant="outline"
                                            className="text-xs flex items-center gap-1"
                                          >
                                            <Target className="h-3 w-3" />
                                            {opportunityDetails.focus_area.replace(
                                              /_/g,
                                              " "
                                            )}
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* NPO Comparison Table */}
                                <div className="p-4">
                                  <Tabs
                                    defaultValue="pricing"
                                    className="w-full"
                                  >
                                    <TabsList className="mb-4">
                                      <TabsTrigger value="pricing">
                                        Pricing Comparison
                                      </TabsTrigger>
                                      <TabsTrigger value="details">
                                        Additional Details
                                      </TabsTrigger>
                                      <TabsTrigger value="locations">
                                        Supported Locations
                                      </TabsTrigger>
                                    </TabsList>

                                    {/* Pricing Comparison Tab */}
                                    <TabsContent
                                      value="pricing"
                                      className="mt-0"
                                    >
                                      <div className="overflow-x-auto">
                                        {(() => {
                                          // Sort rate cards based on sortConfig
                                          const sortedRateCards = [
                                            ...rateCards,
                                          ].sort((a: any, b: any) => {
                                            if (!a || !b) return 0;

                                            let aValue: any;
                                            let bValue: any;

                                            // Get the opportunity response data for comparison
                                            const aResponseData =
                                              a.meta?.opportunity_responses?.[
                                                opportunityId
                                              ];
                                            const bResponseData =
                                              b.meta?.opportunity_responses?.[
                                                opportunityId
                                              ];

                                            if (
                                              !aResponseData ||
                                              !bResponseData
                                            )
                                              return 0;

                                            const sortConfig =
                                              getSortConfig(opportunityId);

                                            switch (sortConfig.key) {
                                              case "cost":
                                                // Use centralized function to calculate total cost for sorting
                                                aValue =
                                                  calculateTotalCostPerUnit(
                                                    aResponseData
                                                  );
                                                bValue =
                                                  calculateTotalCostPerUnit(
                                                    bResponseData
                                                  );

                                                // Convert to same currency for comparison if needed
                                                // Simple currency conversion for comparison purposes
                                                if (
                                                  aResponseData.currency !==
                                                  bResponseData.currency
                                                ) {
                                                  // If currencies are different, normalize to USD for comparison
                                                  // This is a simplified approach - in a real app you'd use current exchange rates
                                                  if (
                                                    aResponseData.currency ===
                                                    "INR"
                                                  )
                                                    aValue = aValue / 80; // Approximate INR to USD
                                                  if (
                                                    bResponseData.currency ===
                                                    "INR"
                                                  )
                                                    bValue = bValue / 80; // Approximate INR to USD
                                                }
                                                break;
                                              case "min_volunteers":
                                                aValue =
                                                  aResponseData.min_volunteers ||
                                                  0;
                                                bValue =
                                                  bResponseData.min_volunteers ||
                                                  0;
                                                break;
                                              case "npo":
                                                aValue = a.npo?.org_name || "";
                                                bValue = b.npo?.org_name || "";
                                                break;
                                              default:
                                                return 0;
                                            }

                                            if (aValue < bValue) {
                                              return sortConfig.direction ===
                                                "ascending"
                                                ? -1
                                                : 1;
                                            }
                                            if (aValue > bValue) {
                                              return sortConfig.direction ===
                                                "ascending"
                                                ? 1
                                                : -1;
                                            }
                                            return 0;
                                          });

                                          // Using the global getCurrencySymbol function

                                          return (
                                            <Table>
                                              <TableHeader>
                                                <TableRow className="bg-slate-50">
                                                  <TableHead
                                                    className="w-[180px] cursor-pointer hover:bg-slate-100"
                                                    onClick={() =>
                                                      requestSort(
                                                        opportunityId,
                                                        "npo"
                                                      )
                                                    }
                                                  >
                                                    NPO{" "}
                                                    {getSortConfig(
                                                      opportunityId
                                                    ).key === "npo" && (
                                                      <ChevronUp
                                                        className={`inline h-4 w-4 transition-transform ${
                                                          getSortConfig(
                                                            opportunityId
                                                          ).direction ===
                                                          "descending"
                                                            ? "rotate-180"
                                                            : ""
                                                        }`}
                                                      />
                                                    )}
                                                  </TableHead>
                                                  <TableHead>
                                                    Supported Locations
                                                  </TableHead>
                                                  <TableHead
                                                    className="cursor-pointer hover:bg-slate-100"
                                                    onClick={() =>
                                                      requestSort(
                                                        opportunityId,
                                                        "cost"
                                                      )
                                                    }
                                                  >
                                                    Total Cost{" "}
                                                    {getSortConfig(
                                                      opportunityId
                                                    ).key === "cost" && (
                                                      <ChevronUp
                                                        className={`inline h-4 w-4 transition-transform ${
                                                          getSortConfig(
                                                            opportunityId
                                                          ).direction ===
                                                          "descending"
                                                            ? "rotate-180"
                                                            : ""
                                                        }`}
                                                      />
                                                    )}
                                                  </TableHead>
                                                  <TableHead>Model</TableHead>
                                                  <TableHead
                                                    className="cursor-pointer hover:bg-slate-100"
                                                    onClick={() =>
                                                      requestSort(
                                                        opportunityId,
                                                        "min_volunteers"
                                                      )
                                                    }
                                                  >
                                                    Min-Max Volunteers{" "}
                                                    {getSortConfig(
                                                      opportunityId
                                                    ).key ===
                                                      "min_volunteers" && (
                                                      <ChevronUp
                                                        className={`inline h-4 w-4 transition-transform ${
                                                          getSortConfig(
                                                            opportunityId
                                                          ).direction ===
                                                          "descending"
                                                            ? "rotate-180"
                                                            : ""
                                                        }`}
                                                      />
                                                    )}
                                                  </TableHead>
                                                  <TableHead>
                                                    Breakdown
                                                  </TableHead>
                                                  <TableHead className="text-right">
                                                    Actions
                                                  </TableHead>
                                                </TableRow>
                                              </TableHeader>
                                              <TableBody>
                                                {sortedRateCards.map(
                                                  (
                                                    item: any,
                                                    index: number
                                                  ) => {
                                                    // Get the opportunity response data
                                                    const responseData =
                                                      item.meta
                                                        ?.opportunity_responses?.[
                                                        opportunityId
                                                      ];
                                                    if (!responseData)
                                                      return null;

                                                    // Calculate total additional fees
                                                    let totalAdditionalFees = 0;
                                                    if (
                                                      responseData.additional_fees
                                                    ) {
                                                      const fees =
                                                        responseData.additional_fees;
                                                      if (
                                                        fees.program_management
                                                      )
                                                        totalAdditionalFees +=
                                                          fees
                                                            .program_management
                                                            .amount;
                                                      if (fees.material_kit)
                                                        totalAdditionalFees +=
                                                          fees.material_kit
                                                            .amount;
                                                      if (fees.refreshments)
                                                        totalAdditionalFees +=
                                                          fees.refreshments
                                                            .amount;
                                                      if (fees.goodies)
                                                        totalAdditionalFees +=
                                                          fees.goodies.amount;
                                                    }

                                                    // Get the first supported location or a default message
                                                    const locationInfo =
                                                      item.meta
                                                        ?.supported_locations &&
                                                      item.meta
                                                        .supported_locations
                                                        .length > 0
                                                        ? item.meta
                                                            .supported_locations[0]
                                                        : "No location specified";

                                                    // Count of additional locations
                                                    const additionalLocations =
                                                      item.meta
                                                        ?.supported_locations
                                                        ? Math.max(
                                                            0,
                                                            item.meta
                                                              .supported_locations
                                                              .length - 1
                                                          )
                                                        : 0;

                                                    // Get currency symbol
                                                    const currencySymbol =
                                                      getCurrencySymbol(
                                                        responseData.currency
                                                      );

                                                    // Calculate total volunteer count from campaign metadata
                                                    let totalVolunteerCount = 0;
                                                    if (
                                                      campaign?.meta
                                                        ?.locations &&
                                                      campaign.meta.locations
                                                        .length > 0
                                                    ) {
                                                      campaign.meta.locations.forEach(
                                                        (location) => {
                                                          if (
                                                            location.meta
                                                              ?.volunteers
                                                          ) {
                                                            totalVolunteerCount +=
                                                              location.meta
                                                                .volunteers;
                                                          }
                                                        }
                                                      );
                                                    }

                                                    // If no volunteers specified in metadata, use 100 as fallback
                                                    const volunteerCount =
                                                      totalVolunteerCount > 0
                                                        ? totalVolunteerCount
                                                        : 100;

                                                    // Use centralized function to calculate total estimated cost
                                                    const {
                                                      totalCostEstimate,
                                                      eventsRequired,
                                                    } =
                                                      calculateTotalEstimatedCost(
                                                        responseData,
                                                        volunteerCount
                                                      );

                                                    return (
                                                      <TableRow key={index}>
                                                        <TableCell className="font-medium">
                                                          <div className="flex items-center space-x-2">
                                                            {item.npo
                                                              ?.logo_url ? (
                                                              <img
                                                                src={
                                                                  item.npo
                                                                    .logo_url
                                                                }
                                                                alt={
                                                                  item.npo
                                                                    .org_name
                                                                }
                                                                className="h-8 w-8 rounded-full object-cover"
                                                              />
                                                            ) : (
                                                              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                                                <Building2 className="h-4 w-4 text-primary" />
                                                              </div>
                                                            )}
                                                            <div>
                                                              <div className="font-medium">
                                                                {item.npo
                                                                  ?.org_name ||
                                                                  "Unknown NPO"}
                                                              </div>
                                                              <div className="flex flex-col space-y-1 mt-1">
                                                                {item.npo
                                                                  ?.website && (
                                                                  <a
                                                                    href={
                                                                      item.npo
                                                                        .website
                                                                    }
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-xs text-muted-foreground hover:underline flex items-center"
                                                                  >
                                                                    <Globe className="h-3 w-3 mr-1" />
                                                                    Website{" "}
                                                                    <ExternalLink className="h-3 w-3 ml-1" />
                                                                  </a>
                                                                )}
                                                                {item.npo
                                                                  ?.country && (
                                                                  <span className="text-xs text-muted-foreground flex items-center">
                                                                    <MapPin className="h-3 w-3 mr-1" />
                                                                    {[
                                                                      item.npo
                                                                        .city,
                                                                      item.npo
                                                                        .state,
                                                                      item.npo
                                                                        .country,
                                                                    ]
                                                                      .filter(
                                                                        Boolean
                                                                      )
                                                                      .join(
                                                                        ", "
                                                                      )}
                                                                  </span>
                                                                )}
                                                              </div>
                                                            </div>
                                                          </div>
                                                        </TableCell>
                                                        <TableCell>
                                                          <div className="flex items-center">
                                                            <MapPin className="h-3 w-3 mr-1 text-primary" />
                                                            <span className="text-sm">
                                                              {locationInfo}
                                                            </span>
                                                          </div>
                                                          {additionalLocations >
                                                            0 && (
                                                            <Badge
                                                              variant="outline"
                                                              className="mt-1 text-xs"
                                                            >
                                                              +
                                                              {
                                                                additionalLocations
                                                              }{" "}
                                                              more location
                                                              {additionalLocations !==
                                                              1
                                                                ? "s"
                                                                : ""}
                                                            </Badge>
                                                          )}
                                                        </TableCell>
                                                        <TableCell>
                                                          <div className="font-medium">
                                                            {(() => {
                                                              // Use centralized function to calculate total cost
                                                              const totalBasePrice =
                                                                calculateTotalCostPerUnit(
                                                                  responseData
                                                                );
                                                              const totalAdditionalFees =
                                                                calculateTotalAdditionalFees(
                                                                  responseData
                                                                );

                                                              return (
                                                                <>
                                                                  {
                                                                    currencySymbol
                                                                  }
                                                                  {totalBasePrice.toLocaleString(
                                                                    undefined,
                                                                    {
                                                                      minimumFractionDigits:
                                                                        totalBasePrice %
                                                                          1 ===
                                                                        0
                                                                          ? 0
                                                                          : 2,
                                                                      maximumFractionDigits: 2,
                                                                    }
                                                                  )}
                                                                  {!responseData.include_fees_in_base &&
                                                                    totalAdditionalFees >
                                                                      0 && (
                                                                      <span className="text-xs text-muted-foreground ml-1">
                                                                        (includes
                                                                        fees)
                                                                      </span>
                                                                    )}
                                                                </>
                                                              );
                                                            })()}
                                                          </div>
                                                          <div className="text-xs text-muted-foreground mt-1">
                                                            Est.{" "}
                                                            {currencySymbol}
                                                            {totalCostEstimate.toLocaleString(
                                                              undefined,
                                                              {
                                                                minimumFractionDigits: 0,
                                                                maximumFractionDigits: 0,
                                                              }
                                                            )}{" "}
                                                            for{" "}
                                                            {volunteerCount.toLocaleString()}{" "}
                                                            volunteer
                                                            {volunteerCount !==
                                                            1
                                                              ? "s"
                                                              : ""}
                                                            {responseData.pricing_model ===
                                                              "per_event" &&
                                                              parseInt(
                                                                responseData.max_volunteers_per_event
                                                              ) > 0 && (
                                                                <span className="block">
                                                                  (
                                                                  {Math.ceil(
                                                                    volunteerCount /
                                                                      parseInt(
                                                                        responseData.max_volunteers_per_event
                                                                      )
                                                                  )}{" "}
                                                                  event
                                                                  {Math.ceil(
                                                                    volunteerCount /
                                                                      parseInt(
                                                                        responseData.max_volunteers_per_event
                                                                      )
                                                                  ) !== 1
                                                                    ? "s"
                                                                    : ""}{" "}
                                                                  ×{" "}
                                                                  {
                                                                    currencySymbol
                                                                  }
                                                                  {responseData.cost_per_unit.toLocaleString(
                                                                    undefined,
                                                                    {
                                                                      minimumFractionDigits: 0,
                                                                      maximumFractionDigits: 0,
                                                                    }
                                                                  )}
                                                                  )
                                                                </span>
                                                              )}
                                                          </div>
                                                          {responseData.include_fees_in_base && (
                                                            <Badge
                                                              variant="secondary"
                                                              className="text-xs mt-1"
                                                            >
                                                              All-inclusive
                                                            </Badge>
                                                          )}
                                                        </TableCell>
                                                        <TableCell>
                                                          {responseData.pricing_model ===
                                                          "per_volunteer"
                                                            ? "Per Volunteer"
                                                            : responseData.pricing_model ===
                                                              "per_event"
                                                            ? "Per Event"
                                                            : "Total"}
                                                        </TableCell>
                                                        <TableCell>
                                                          {
                                                            responseData.min_volunteers
                                                          }{" "}
                                                          -{" "}
                                                          {responseData.max_volunteers_per_event ||
                                                            "No limit"}
                                                        </TableCell>
                                                        <TableCell>
                                                          {totalAdditionalFees >
                                                          0 ? (
                                                            <Popover>
                                                              <PopoverTrigger
                                                                asChild
                                                              >
                                                                <Button
                                                                  variant="outline"
                                                                  size="sm"
                                                                >
                                                                  {
                                                                    currencySymbol
                                                                  }
                                                                  {totalAdditionalFees.toLocaleString(
                                                                    undefined,
                                                                    {
                                                                      minimumFractionDigits:
                                                                        totalAdditionalFees %
                                                                          1 ===
                                                                        0
                                                                          ? 0
                                                                          : 2,
                                                                      maximumFractionDigits: 2,
                                                                    }
                                                                  )}{" "}
                                                                  <ChevronDown className="h-3 w-3 ml-1" />
                                                                </Button>
                                                              </PopoverTrigger>
                                                              <PopoverContent className="w-80 bg-white border shadow-md">
                                                                <div className="space-y-2 p-3 bg-slate-50 rounded-md">
                                                                  <h4 className="font-medium text-sm">
                                                                    Additional
                                                                    Fees
                                                                    Breakdown
                                                                  </h4>
                                                                  <div className="space-y-1">
                                                                    {responseData
                                                                      .additional_fees
                                                                      .program_management &&
                                                                      responseData
                                                                        .additional_fees
                                                                        .program_management
                                                                        .amount >
                                                                        0 && (
                                                                        <div className="flex justify-between text-sm">
                                                                          <span className="flex items-center">
                                                                            <Settings className="h-3 w-3 mr-1 text-primary" />{" "}
                                                                            Program
                                                                            Management
                                                                          </span>
                                                                          <span>
                                                                            {
                                                                              currencySymbol
                                                                            }
                                                                            {responseData.additional_fees.program_management.amount.toLocaleString(
                                                                              undefined,
                                                                              {
                                                                                minimumFractionDigits:
                                                                                  responseData
                                                                                    .additional_fees
                                                                                    .program_management
                                                                                    .amount %
                                                                                    1 ===
                                                                                  0
                                                                                    ? 0
                                                                                    : 2,
                                                                                maximumFractionDigits: 2,
                                                                              }
                                                                            )}
                                                                            <span className="text-xs text-muted-foreground ml-1">
                                                                              (
                                                                              {responseData.additional_fees.program_management.fee_type.replace(
                                                                                /_/g,
                                                                                " "
                                                                              )}
                                                                              )
                                                                            </span>
                                                                          </span>
                                                                        </div>
                                                                      )}
                                                                    {responseData
                                                                      .additional_fees
                                                                      .material_kit &&
                                                                      responseData
                                                                        .additional_fees
                                                                        .material_kit
                                                                        .amount >
                                                                        0 && (
                                                                        <div className="flex justify-between text-sm">
                                                                          <span className="flex items-center">
                                                                            <Package className="h-3 w-3 mr-1 text-primary" />{" "}
                                                                            Material/Kit
                                                                          </span>
                                                                          <span>
                                                                            {
                                                                              currencySymbol
                                                                            }
                                                                            {responseData.additional_fees.material_kit.amount.toLocaleString(
                                                                              undefined,
                                                                              {
                                                                                minimumFractionDigits:
                                                                                  responseData
                                                                                    .additional_fees
                                                                                    .material_kit
                                                                                    .amount %
                                                                                    1 ===
                                                                                  0
                                                                                    ? 0
                                                                                    : 2,
                                                                                maximumFractionDigits: 2,
                                                                              }
                                                                            )}
                                                                            <span className="text-xs text-muted-foreground ml-1">
                                                                              (
                                                                              {responseData.additional_fees.material_kit.fee_type.replace(
                                                                                /_/g,
                                                                                " "
                                                                              )}
                                                                              )
                                                                            </span>
                                                                          </span>
                                                                        </div>
                                                                      )}
                                                                    {responseData
                                                                      .additional_fees
                                                                      .refreshments &&
                                                                      responseData
                                                                        .additional_fees
                                                                        .refreshments
                                                                        .amount >
                                                                        0 && (
                                                                        <div className="flex justify-between text-sm">
                                                                          <span className="flex items-center">
                                                                            <Coffee className="h-3 w-3 mr-1 text-primary" />{" "}
                                                                            Refreshments
                                                                          </span>
                                                                          <span>
                                                                            {
                                                                              currencySymbol
                                                                            }
                                                                            {responseData.additional_fees.refreshments.amount.toLocaleString(
                                                                              undefined,
                                                                              {
                                                                                minimumFractionDigits:
                                                                                  responseData
                                                                                    .additional_fees
                                                                                    .refreshments
                                                                                    .amount %
                                                                                    1 ===
                                                                                  0
                                                                                    ? 0
                                                                                    : 2,
                                                                                maximumFractionDigits: 2,
                                                                              }
                                                                            )}
                                                                            <span className="text-xs text-muted-foreground ml-1">
                                                                              (
                                                                              {responseData.additional_fees.refreshments.fee_type.replace(
                                                                                /_/g,
                                                                                " "
                                                                              )}
                                                                              )
                                                                            </span>
                                                                          </span>
                                                                        </div>
                                                                      )}
                                                                    {responseData
                                                                      .additional_fees
                                                                      .goodies &&
                                                                      responseData
                                                                        .additional_fees
                                                                        .goodies
                                                                        .amount >
                                                                        0 && (
                                                                        <div className="flex justify-between text-sm">
                                                                          <span className="flex items-center">
                                                                            <Gift className="h-3 w-3 mr-1 text-primary" />{" "}
                                                                            Goodies
                                                                          </span>
                                                                          <span>
                                                                            {
                                                                              currencySymbol
                                                                            }
                                                                            {responseData.additional_fees.goodies.amount.toLocaleString(
                                                                              undefined,
                                                                              {
                                                                                minimumFractionDigits:
                                                                                  responseData
                                                                                    .additional_fees
                                                                                    .goodies
                                                                                    .amount %
                                                                                    1 ===
                                                                                  0
                                                                                    ? 0
                                                                                    : 2,
                                                                                maximumFractionDigits: 2,
                                                                              }
                                                                            )}
                                                                            <span className="text-xs text-muted-foreground ml-1">
                                                                              (
                                                                              {responseData.additional_fees.goodies.fee_type.replace(
                                                                                /_/g,
                                                                                " "
                                                                              )}
                                                                              )
                                                                            </span>
                                                                          </span>
                                                                        </div>
                                                                      )}
                                                                  </div>
                                                                </div>
                                                              </PopoverContent>
                                                            </Popover>
                                                          ) : (
                                                            <span className="text-muted-foreground">
                                                              None
                                                            </span>
                                                          )}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                          <div className="flex justify-end space-x-2">
                                                            <Button
                                                              variant="default"
                                                              size="sm"
                                                              className="flex items-center"
                                                              onClick={() =>
                                                                handleAccept(
                                                                  item.id
                                                                )
                                                              }
                                                              disabled={
                                                                isAccepting
                                                              }
                                                            >
                                                              {isAccepting ? (
                                                                <>
                                                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                                                  Accept
                                                                </>
                                                              ) : (
                                                                <>
                                                                  <CheckCircle className="h-3 w-3 mr-1" />
                                                                  Accept
                                                                </>
                                                              )}
                                                            </Button>
                                                            <Button
                                                              variant="outline"
                                                              size="sm"
                                                              className="flex items-center text-destructive hover:text-destructive"
                                                              onClick={() =>
                                                                openRejectDialog(
                                                                  item.id
                                                                )
                                                              }
                                                              disabled={
                                                                isRejecting
                                                              }
                                                            >
                                                              <X className="h-3 w-3 mr-1" />
                                                              Decline
                                                            </Button>
                                                          </div>
                                                        </TableCell>
                                                      </TableRow>
                                                    );
                                                  }
                                                )}
                                              </TableBody>
                                            </Table>
                                          );
                                        })()}
                                      </div>
                                    </TabsContent>

                                    {/* Additional Details Tab */}
                                    <TabsContent
                                      value="details"
                                      className="mt-0"
                                    >
                                      <div className="space-y-4">
                                        {rateCards.map(
                                          (item: any, index: number) => {
                                            const responseData =
                                              item.meta
                                                ?.opportunity_responses?.[
                                                opportunityId
                                              ];
                                            if (
                                              !responseData ||
                                              !responseData.additional_details
                                            )
                                              return null;

                                            return (
                                              <div
                                                key={index}
                                                className="border rounded-md p-4"
                                              >
                                                <div className="flex items-center space-x-2 mb-2">
                                                  {item.npo?.logo_url ? (
                                                    <img
                                                      src={item.npo.logo_url}
                                                      alt={item.npo.org_name}
                                                      className="h-8 w-8 rounded-full object-cover"
                                                    />
                                                  ) : (
                                                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                                      <Building2 className="h-4 w-4 text-primary" />
                                                    </div>
                                                  )}
                                                  <h4 className="font-medium">
                                                    {item.npo?.org_name ||
                                                      "Unknown NPO"}
                                                  </h4>
                                                </div>
                                                <div className="bg-slate-50 p-3 rounded-md">
                                                  <p className="text-sm">
                                                    {
                                                      responseData.additional_details
                                                    }
                                                  </p>
                                                </div>
                                              </div>
                                            );
                                          }
                                        )}
                                        {!rateCards.some((item: any) => {
                                          const responseData =
                                            item.meta?.opportunity_responses?.[
                                              opportunityId
                                            ];
                                          return (
                                            responseData &&
                                            responseData.additional_details
                                          );
                                        }) && (
                                          <div className="text-center py-6 bg-slate-50 rounded-md">
                                            <Info className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                                            <p className="text-muted-foreground">
                                              No additional details provided by
                                              any NPO
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </TabsContent>

                                    {/* Supported Locations Tab */}
                                    <TabsContent
                                      value="locations"
                                      className="mt-0"
                                    >
                                      <div className="space-y-4">
                                        {rateCards.map(
                                          (item: any, index: number) => {
                                            if (
                                              !item.meta?.supported_locations ||
                                              item.meta.supported_locations
                                                .length === 0
                                            )
                                              return null;

                                            return (
                                              <div
                                                key={index}
                                                className="border rounded-md p-4"
                                              >
                                                <div className="flex items-center space-x-2 mb-2">
                                                  {item.npo?.logo_url ? (
                                                    <img
                                                      src={item.npo.logo_url}
                                                      alt={item.npo.org_name}
                                                      className="h-8 w-8 rounded-full object-cover"
                                                    />
                                                  ) : (
                                                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                                      <Building2 className="h-4 w-4 text-primary" />
                                                    </div>
                                                  )}
                                                  <div>
                                                    <h4 className="font-medium">
                                                      {item.npo?.org_name ||
                                                        "Unknown NPO"}
                                                    </h4>
                                                    <p className="text-xs text-muted-foreground">
                                                      Supports{" "}
                                                      {
                                                        item.meta
                                                          .supported_locations
                                                          .length
                                                      }{" "}
                                                      location
                                                      {item.meta
                                                        .supported_locations
                                                        .length !== 1
                                                        ? "s"
                                                        : ""}
                                                    </p>
                                                  </div>
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                                                  {item.meta.supported_locations.map(
                                                    (
                                                      locationName: string,
                                                      idx: number
                                                    ) => (
                                                      <div
                                                        key={idx}
                                                        className="flex items-center bg-slate-50 p-2 rounded-md"
                                                      >
                                                        <MapPin className="h-3 w-3 mr-2 text-primary" />
                                                        <span className="text-xs">
                                                          {locationName}
                                                        </span>
                                                      </div>
                                                    )
                                                  )}
                                                </div>
                                              </div>
                                            );
                                          }
                                        )}
                                        {!rateCards.some(
                                          (item: any) =>
                                            item.meta?.supported_locations &&
                                            item.meta.supported_locations
                                              .length > 0
                                        ) && (
                                          <div className="text-center py-6 bg-slate-50 rounded-md">
                                            <MapPinned className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                                            <p className="text-muted-foreground">
                                              No location information provided
                                              by any NPO
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </TabsContent>
                                  </Tabs>
                                </div>
                              </div>
                            );
                          }
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-slate-50 rounded-md">
                      <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium mb-2">
                        Awaiting Responses
                      </h3>
                      <p className="text-muted-foreground max-w-md mx-auto">
                        NPOs are currently reviewing your campaign details and
                        preparing their responses. Check back later for pricing
                        information.
                      </p>

                      <div className="mt-6 space-y-2">
                        <h4 className="text-sm font-medium">
                          Additional Notes for NPOs:
                        </h4>
                        <div className="bg-white p-3 rounded-md text-sm max-w-md mx-auto">
                          {campaign?.rfq?.meta?.additional_notes ? (
                            campaign.rfq.meta.additional_notes
                          ) : (
                            <span className="text-muted-foreground italic">
                              No additional notes provided
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

      {/* Reject Dialog */}
      <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline RFQ Response</DialogTitle>
            <DialogDescription>
              Please provide a reason for declining this response. This
              information will be shared with the NPO.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reject-reason">Reason for declining</Label>
            <Textarea
              id="reject-reason"
              placeholder="Enter your reason for declining this response..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="mt-2"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsRejectDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={isRejecting}
            >
              {isRejecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Declining...
                </>
              ) : (
                "Decline Response"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rate Card Modal */}
      <Dialog open={isRateCardModalOpen} onOpenChange={setIsRateCardModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedNpoProfile?.logo_url && (
                <img
                  src={selectedNpoProfile.logo_url}
                  alt={selectedNpoProfile.org_name}
                  className="h-6 w-6 rounded-full object-cover"
                />
              )}
              {selectedNpoProfile?.org_name} Rate Cards
            </DialogTitle>
            <DialogDescription>
              Detailed pricing information for all opportunities
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {selectedNpoRateCards.length > 0 ? (
              <div className="space-y-6">
                {/* NPO Profile Summary */}
                {selectedNpoProfile && (
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <div className="flex items-start gap-4">
                      {selectedNpoProfile.logo_url && (
                        <img
                          src={selectedNpoProfile.logo_url}
                          alt={selectedNpoProfile.org_name}
                          className="h-16 w-16 rounded-lg object-cover"
                        />
                      )}
                      <div>
                        <h3 className="text-lg font-semibold">
                          {selectedNpoProfile.org_name}
                        </h3>
                        {selectedNpoProfile.tagline && (
                          <p className="text-sm text-muted-foreground">
                            {selectedNpoProfile.tagline}
                          </p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-2">
                          {selectedNpoProfile.focus_areas?.map(
                            (area: string, i: number) => (
                              <Badge
                                key={i}
                                variant="outline"
                                className="text-xs"
                              >
                                {area.replace(/_/g, " ")}
                              </Badge>
                            )
                          )}
                        </div>
                        <div className="mt-3 flex gap-4">
                          {selectedNpoProfile.website && (
                            <a
                              href={selectedNpoProfile.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs flex items-center gap-1 text-blue-600 hover:underline"
                            >
                              <Globe className="h-3 w-3" /> Website
                            </a>
                          )}
                          {selectedNpoProfile.contact_email && (
                            <span className="text-xs flex items-center gap-1 text-muted-foreground">
                              <Mail className="h-3 w-3" />{" "}
                              {selectedNpoProfile.contact_email}
                            </span>
                          )}
                          {selectedNpoProfile.contact_phone && (
                            <span className="text-xs flex items-center gap-1 text-muted-foreground">
                              <Phone className="h-3 w-3" />{" "}
                              {selectedNpoProfile.contact_phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rate Cards by Opportunity */}
                <Accordion type="multiple" defaultValue={[]}>
                  {campaign?.meta?.opportunities?.map((opportunityId) => {
                    const opportunityDetails = opportunitiesData[opportunityId];
                    if (!opportunityDetails) return null;

                    // Filter rate cards for this opportunity
                    const opportunityRateCards = selectedNpoRateCards.filter(
                      (item: any) =>
                        item.meta?.opportunity_responses &&
                        item.meta.opportunity_responses[opportunityId]
                    );

                    if (opportunityRateCards.length === 0) return null;

                    // Calculate total price for all rate cards in this opportunity
                    const totalPrices = opportunityRateCards.map(
                      (item: any) => {
                        const responseData =
                          item.meta?.opportunity_responses?.[opportunityId];
                        if (!responseData) return { price: 0, currency: "USD" };

                        // Calculate base price
                        let totalPrice = responseData.cost_per_unit || 0;

                        // Add additional fees if not included in base price
                        if (
                          responseData.additional_fees &&
                          !responseData.include_fees_in_base
                        ) {
                          const fees = responseData.additional_fees;
                          if (fees.program_management)
                            totalPrice += fees.program_management.amount;
                          if (fees.material_kit)
                            totalPrice += fees.material_kit.amount;
                          if (fees.refreshments)
                            totalPrice += fees.refreshments.amount;
                          if (fees.goodies) totalPrice += fees.goodies.amount;
                        }

                        return {
                          price: totalPrice,
                          currency: responseData.currency || "USD",
                          status: responseData.status || "pending",
                        };
                      }
                    );

                    return (
                      <AccordionItem value={opportunityId} key={opportunityId}>
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center justify-between w-full pr-4">
                            <div className="flex items-center gap-2">
                              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <Lightbulb className="h-4 w-4 text-primary" />
                              </div>
                              <span>{opportunityDetails.name}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              {totalPrices.map((priceInfo, idx) => {
                                const currencySymbol = getCurrencySymbol(
                                  priceInfo.currency
                                );
                                return (
                                  <div key={idx} className="flex items-center">
                                    <Badge
                                      variant={
                                        priceInfo.status === "accepted"
                                          ? "success"
                                          : priceInfo.status === "rejected"
                                          ? "destructive"
                                          : "outline"
                                      }
                                      className="text-xs"
                                    >
                                      {currencySymbol}
                                      {priceInfo.price.toLocaleString()}
                                    </Badge>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-4">
                            {opportunityRateCards.map(
                              (item: any, index: number) => {
                                const responseData =
                                  item.meta?.opportunity_responses?.[
                                    opportunityId
                                  ];
                                if (!responseData) return null;

                                // Get currency symbol
                                const currencySymbol = getCurrencySymbol(
                                  responseData.currency
                                );

                                return (
                                  <Card key={index} className="overflow-hidden">
                                    <CardHeader className="pb-2">
                                      <div className="flex items-center justify-between">
                                        <CardTitle className="text-base flex items-center gap-2">
                                          <DollarSign className="h-4 w-4 text-green-600" />
                                          Rate Card
                                          {responseData.status && (
                                            <Badge
                                              variant={
                                                responseData.status ===
                                                "accepted"
                                                  ? "success"
                                                  : responseData.status ===
                                                    "rejected"
                                                  ? "destructive"
                                                  : "outline"
                                              }
                                              className="ml-2 text-xs"
                                            >
                                              {responseData.status
                                                .charAt(0)
                                                .toUpperCase() +
                                                responseData.status.slice(1)}
                                            </Badge>
                                          )}
                                        </CardTitle>
                                        <div className="text-sm text-muted-foreground">
                                          Submitted:{" "}
                                          {item.created_at
                                            ? format(
                                                new Date(item.created_at),
                                                "MMM d, yyyy"
                                              )
                                            : "N/A"}
                                        </div>
                                      </div>
                                      <CardDescription>
                                        {responseData.pricing_model ===
                                        "per_volunteer"
                                          ? "Per Volunteer Pricing"
                                          : responseData.pricing_model ===
                                            "per_event"
                                          ? "Per Event Pricing"
                                          : "Fixed Total Program Pricing"}
                                      </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                      <div className="space-y-4">
                                        {/* Base Pricing */}
                                        <div className="bg-slate-50 p-3 rounded-md">
                                          <h4 className="font-medium mb-2 flex items-center gap-2">
                                            <Settings className="h-4 w-4 text-slate-500" />
                                            Base Pricing
                                          </h4>
                                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="p-3 bg-white rounded-md border">
                                              <div className="text-sm text-muted-foreground mb-1">
                                                Base Rate
                                              </div>
                                              <div className="text-xl font-semibold">
                                                {currencySymbol}
                                                {responseData.cost_per_unit.toLocaleString()}
                                                <span className="text-sm font-normal text-muted-foreground ml-1">
                                                  {responseData.pricing_model ===
                                                  "per_volunteer"
                                                    ? "/ volunteer"
                                                    : responseData.pricing_model ===
                                                      "per_event"
                                                    ? "/ event"
                                                    : "total"}
                                                </span>
                                              </div>
                                            </div>

                                            {responseData.pricing_model ===
                                              "per_volunteer" && (
                                              <div className="p-3 bg-white rounded-md border">
                                                <div className="text-sm text-muted-foreground mb-1">
                                                  Min-Max Volunteers
                                                </div>
                                                <div className="text-lg font-medium">
                                                  {responseData.min_volunteers ||
                                                    "N/A"}{" "}
                                                  -{" "}
                                                  {responseData.max_volunteers_per_event ||
                                                    "Unlimited"}
                                                </div>
                                              </div>
                                            )}

                                            {responseData.pricing_model ===
                                              "per_event" && (
                                              <div className="p-3 bg-white rounded-md border">
                                                <div className="text-sm text-muted-foreground mb-1">
                                                  Max Volunteers Per Event
                                                </div>
                                                <div className="text-lg font-medium">
                                                  {responseData.max_volunteers_per_event ||
                                                    "Unlimited"}
                                                </div>
                                              </div>
                                            )}

                                            <div className="p-3 bg-white rounded-md border">
                                              <div className="text-sm text-muted-foreground mb-1">
                                                Currency
                                              </div>
                                              <div className="text-lg font-medium">
                                                {responseData.currency}
                                                {responseData.usd_equivalent &&
                                                  responseData.currency !==
                                                    "USD" && (
                                                    <span className="text-sm text-muted-foreground ml-1">
                                                      (≈ $
                                                      {responseData.usd_equivalent.toLocaleString()}{" "}
                                                      USD)
                                                    </span>
                                                  )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Additional Fees */}
                                        {responseData.additional_fees &&
                                          Object.keys(
                                            responseData.additional_fees
                                          ).length > 0 && (
                                            <div className="bg-slate-50 p-3 rounded-md">
                                              <h4 className="font-medium mb-2 flex items-center gap-2">
                                                <Package className="h-4 w-4 text-slate-500" />
                                                Additional Fees
                                                {responseData.include_fees_in_base && (
                                                  <Badge
                                                    variant="outline"
                                                    className="ml-2 text-xs"
                                                  >
                                                    Included in base price
                                                  </Badge>
                                                )}
                                              </h4>
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {responseData.additional_fees
                                                  .program_management && (
                                                  <div className="p-3 bg-white rounded-md border">
                                                    <div className="text-sm text-muted-foreground mb-1">
                                                      Program Management Fee
                                                    </div>
                                                    <div className="text-lg font-medium">
                                                      {currencySymbol}
                                                      {responseData.additional_fees.program_management.amount.toLocaleString()}
                                                      <span className="text-sm text-muted-foreground ml-1">
                                                        (
                                                        {responseData.additional_fees.program_management.fee_type.replace(
                                                          "_",
                                                          " "
                                                        )}
                                                        )
                                                      </span>
                                                    </div>
                                                  </div>
                                                )}

                                                {responseData.additional_fees
                                                  .material_kit && (
                                                  <div className="p-3 bg-white rounded-md border">
                                                    <div className="text-sm text-muted-foreground mb-1">
                                                      Material/Kit Fee
                                                    </div>
                                                    <div className="text-lg font-medium">
                                                      {currencySymbol}
                                                      {responseData.additional_fees.material_kit.amount.toLocaleString()}
                                                      <span className="text-sm text-muted-foreground ml-1">
                                                        (
                                                        {responseData.additional_fees.material_kit.fee_type.replace(
                                                          "_",
                                                          " "
                                                        )}
                                                        )
                                                      </span>
                                                    </div>
                                                  </div>
                                                )}

                                                {responseData.additional_fees
                                                  .refreshments && (
                                                  <div className="p-3 bg-white rounded-md border">
                                                    <div className="text-sm text-muted-foreground mb-1">
                                                      <Coffee className="h-3 w-3 inline mr-1" />
                                                      Refreshments Fee
                                                    </div>
                                                    <div className="text-lg font-medium">
                                                      {currencySymbol}
                                                      {responseData.additional_fees.refreshments.amount.toLocaleString()}
                                                      <span className="text-sm text-muted-foreground ml-1">
                                                        (
                                                        {responseData.additional_fees.refreshments.fee_type.replace(
                                                          "_",
                                                          " "
                                                        )}
                                                        )
                                                      </span>
                                                    </div>
                                                  </div>
                                                )}

                                                {responseData.additional_fees
                                                  .goodies && (
                                                  <div className="p-3 bg-white rounded-md border">
                                                    <div className="text-sm text-muted-foreground mb-1">
                                                      <Gift className="h-3 w-3 inline mr-1" />
                                                      Goodies Fee
                                                    </div>
                                                    <div className="text-lg font-medium">
                                                      {currencySymbol}
                                                      {responseData.additional_fees.goodies.amount.toLocaleString()}
                                                      <span className="text-sm text-muted-foreground ml-1">
                                                        (
                                                        {responseData.additional_fees.goodies.fee_type.replace(
                                                          "_",
                                                          " "
                                                        )}
                                                        )
                                                      </span>
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          )}

                                        {/* Geographic Pricing */}
                                        {responseData.geographic_pricing &&
                                          responseData.geographic_pricing
                                            .length > 0 && (
                                            <div className="bg-slate-50 p-3 rounded-md">
                                              <h4 className="font-medium mb-2 flex items-center gap-2">
                                                <MapPinned className="h-4 w-4 text-slate-500" />
                                                Geographic Pricing Adjustments
                                              </h4>
                                              <div className="space-y-2">
                                                {responseData.geographic_pricing.map(
                                                  (pricing: any, i: number) => (
                                                    <div
                                                      key={i}
                                                      className="p-3 bg-white rounded-md border flex justify-between items-center"
                                                    >
                                                      <div>
                                                        <div className="font-medium">
                                                          {
                                                            pricing.location_name
                                                          }
                                                        </div>
                                                        <div className="text-sm text-muted-foreground">
                                                          {
                                                            pricing.markup_percentage
                                                          }
                                                          % markup
                                                        </div>
                                                      </div>
                                                      <div className="text-right">
                                                        <div className="text-lg font-medium">
                                                          {currencySymbol}
                                                          {pricing.adjusted_total.toLocaleString()}
                                                        </div>
                                                        <div className="text-sm text-muted-foreground">
                                                          +{currencySymbol}
                                                          {pricing.markup_amount.toLocaleString()}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  )
                                                )}
                                              </div>
                                            </div>
                                          )}

                                        {/* Opportunity-specific Pricing */}
                                        {responseData.opportunity_specific_pricing && (
                                          <div className="bg-slate-50 p-3 rounded-md">
                                            <h4 className="font-medium mb-2 flex items-center gap-2">
                                              <Lightbulb className="h-4 w-4 text-slate-500" />
                                              Opportunity-specific Pricing
                                            </h4>
                                            <div className="p-3 bg-white rounded-md border">
                                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                {responseData
                                                  .opportunity_specific_pricing
                                                  .format && (
                                                  <div>
                                                    <div className="text-sm text-muted-foreground mb-1">
                                                      Format Pricing
                                                    </div>
                                                    <div className="space-y-1">
                                                      {Object.entries(
                                                        responseData
                                                          .opportunity_specific_pricing
                                                          .format
                                                      ).map(
                                                        ([format, price]: [
                                                          string,
                                                          any
                                                        ]) => (
                                                          <div
                                                            key={format}
                                                            className="flex justify-between"
                                                          >
                                                            <span className="flex items-center gap-1">
                                                              {format ===
                                                                "virtual" && (
                                                                <Video className="h-3 w-3" />
                                                              )}
                                                              {format ===
                                                                "in_office" && (
                                                                <Building className="h-3 w-3" />
                                                              )}
                                                              {format ===
                                                                "outdoor" && (
                                                                <Tent className="h-3 w-3" />
                                                              )}
                                                              {format.replace(
                                                                "_",
                                                                " "
                                                              )}
                                                            </span>
                                                            <span className="font-medium">
                                                              {currencySymbol}
                                                              {price.toLocaleString()}
                                                            </span>
                                                          </div>
                                                        )
                                                      )}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        )}

                                        {/* Notes */}
                                        {responseData.notes && (
                                          <div className="bg-slate-50 p-3 rounded-md">
                                            <h4 className="font-medium mb-2 flex items-center gap-2">
                                              <FileText className="h-4 w-4 text-slate-500" />
                                              Additional Notes
                                            </h4>
                                            <div className="p-3 bg-white rounded-md border">
                                              <p className="text-sm whitespace-pre-wrap">
                                                {responseData.notes}
                                              </p>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </CardContent>

                                    {responseData.status === "pending" && (
                                      <CardFooter className="border-t bg-slate-50 flex justify-end gap-2 pt-3 pb-3">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() =>
                                            openRejectDialog(item.id)
                                          }
                                        >
                                          Decline
                                        </Button>
                                        <Button
                                          variant="default"
                                          size="sm"
                                          onClick={() => handleAccept(item.id)}
                                        >
                                          Accept
                                        </Button>
                                      </CardFooter>
                                    )}
                                  </Card>
                                );
                              }
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>
            ) : (
              <div className="text-center py-8">
                <Info className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  No Rate Cards Available
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  There are no rate cards available for this NPO partner.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setIsRateCardModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
